import BaseDetailView from '../shared/BaseDetailView';
import Badge from '../shared/Badge';
import DetailViewActionButtons from '../shared/DetailViewActionButtons';
import NavigationArrow from '../shared/NavigationArrow';
import UserAccessTab from './UserAccessTab';
import UserActivityTab from './UserActivityTab';
import DetailsTabLayout, { FieldSection, SectionDivider } from '../shared/DetailsTabLayout';
import { useEditableState } from '../../hooks/useEditableState';
import { useDetailViewActions } from '../../hooks/useDetailViewActions.jsx';
import { useToggleField } from '../../hooks/useToggleField';
import { ICONS } from '../../constants/detailViewConstants';
import { getThemeBadgeVariant, getSubTheme } from '../../constants/subThemes';
import { capitalizeFirstLetter } from '../../utils/textFormat';
import { formatTimestamp } from '../../utils/activityHelpers';

export default function UserDetailView({ userId, user, onUpdate, onDelete, onPrevious = null, onNext = null, theme = 'purple' }) {
  // Get complete theme configuration
  const themeConfig = getSubTheme(theme);
  const {
    isEditMode,
    isSaving,
    editedData: editedUser,
    handleSave,
    handleCancel,
    handleEdit,
    updateField,
    toggleField
  } = useEditableState(user, onUpdate);

  const {
    createDeleteAction,
    createExportAction
  } = useDetailViewActions();

  const handleToggleActive = useToggleField(user, 'is_active', onUpdate, 'Failed to toggle active state');

  // Create action buttons for the header
  const headerActions = user ? [
    createExportAction(user, `user-${user.username}.json`, 'Export user data'),
    {
      id: 'toggle',
      onClick: handleToggleActive,
      className: "p-2 rounded-lg hover:bg-gray-700/50 text-gray-400 hover:text-gray-300 transition-all duration-200",
      tooltip: user.is_active ? 'Deactivate user' : 'Activate user',
      customIcon: user.is_active ? (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      )
    },
    ...(onDelete ? [createDeleteAction(
      () => onDelete(userId),
      `Are you sure you want to delete ${user.full_name || user.username}? This action cannot be undone.`,
      'Delete user'
    )] : [])
  ] : [];

  // Custom tabs for users
  const tabs = [
    { id: 'details', label: 'Details', icon: ICONS.details },
    { id: 'access', label: 'Access', icon: ICONS.access },
    { id: 'activity', label: 'Activity', icon: ICONS.activity }
  ];

  // Header content with avatar, title, and badges
  const headerContent = (
    <>
      <div className="flex items-center gap-4 mb-2">
        {/* Navigation Arrow - Previous */}
        {(onPrevious || onNext) && (
          <NavigationArrow
            direction="previous"
            onClick={onPrevious}
            disabled={!onPrevious}
            label={onPrevious ? 'Previous user' : 'No previous user'}
          />
        )}

        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center font-bold text-2xl" style={{ color: 'var(--color-text-on-accent)' }}>
          {user?.full_name?.charAt(0).toUpperCase() || user?.username.charAt(0).toUpperCase()}
        </div>
        <div>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{capitalizeFirstLetter(user?.full_name || user?.username)}</h2>
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>@{user?.username}</p>
        </div>

        {/* Navigation Arrow - Next */}
        {(onPrevious || onNext) && (
          <NavigationArrow
            direction="next"
            onClick={onNext}
            disabled={!onNext}
            label={onNext ? 'Next user' : 'No next user'}
          />
        )}
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <Badge variant="gray">
          {user?.is_active ? 'active' : 'inactive'}
        </Badge>
        {user?.role && (
          <Badge variant="gray">
            {user?.role}
          </Badge>
        )}
        <Badge variant="gray">
          last login: {user?.last_login ? formatTimestamp(user.last_login) : 'never'}
        </Badge>
      </div>
    </>
  );

  // Render content based on active tab
  const renderContent = (activeTab) => {
    if (activeTab === 'details') {
      return (
        <DetailsTabLayout
          title="User Details"
          subtitle="Manage user information and permissions"
          isEditMode={isEditMode}
          isSaving={isSaving}
          onEdit={handleEdit}
          onSave={handleSave}
          onCancel={handleCancel}
        >
          <FieldSection label="Full Name">
            <input
              type="text"
              value={isEditMode ? (editedUser?.full_name || '') : (user?.full_name || '')}
              onChange={(e) => updateField('full_name', e.target.value)}
              disabled={!isEditMode}
              className={`w-full h-11 bg-gray-800/50 border border-gray-700/50 rounded-lg px-4 text-white text-sm focus:outline-none ${themeConfig.focus} focus:bg-gray-800 transition-all disabled:opacity-50`}
            />
          </FieldSection>

          <SectionDivider />

          <FieldSection label="Email">
            <input
              type="email"
              value={isEditMode ? (editedUser?.email || '') : (user?.email || '')}
              onChange={(e) => updateField('email', e.target.value)}
              disabled={!isEditMode}
              className={`w-full h-11 bg-gray-800/50 border border-gray-700/50 rounded-lg px-4 text-white text-sm focus:outline-none ${themeConfig.focus} focus:bg-gray-800 transition-all disabled:opacity-50`}
            />
          </FieldSection>
        </DetailsTabLayout>
      );
    }

    if (activeTab === 'access') {
      return <UserAccessTab userId={userId} />;
    }

    if (activeTab === 'activity') {
      return <UserActivityTab userId={userId} />;
    }

    return null;
  };

  return (
    <BaseDetailView
      item={user}
      emptyStateIcon={ICONS.user}
      emptyStateTitle="No user selected"
      emptyStateSubtitle="Select a user from the sidebar to view details"
      headerContent={headerContent}
      headerActions={<DetailViewActionButtons actions={headerActions} />}
      tabs={tabs}
      defaultTab="details"
      renderContent={renderContent}
    />
  );
}
